import React, { useContext } from "react";
import { observer } from "mobx-react";
import { Plus } from "lucide-react";
import { AppWorkspaceContext } from "../../stores/AppWorkspace";
import { APP_PAGES } from "../../helpers/constants";
import { Alert, Button, PageHeader } from "../../../ui";
import FindSwapBox from "../FindSwapBox";
import SwapTokenCallout from "../SwapTokenCallout";
import OrderDetail from "../OrderDetail";

/**
 * One swap at a time. This is what a visitor without swap-history access sees:
 * either the single swap their token resolves, or the lookup box. There is
 * deliberately no list here — the server does not send one.
 */
const FindSwapPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const viewed = workspace.viewedOrder;
  const notFound = workspace.publicToken != null && !viewed;

  if (notFound) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Swap not found" />
        <Alert tone="muted">
          That token doesn’t match a swap. Check you copied all of it — the token
          is the only handle on a swap, so a partial one can’t be recovered.
        </Alert>
        <FindSwapBox />
        <Button
          leftIcon={<Plus size={16} />}
          onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}
          className="self-start"
        >
          Start a new swap
        </Button>
      </div>
    );
  }

  if (viewed) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Your swap"
          subtitle="Leave this page open for live updates."
          actions={
            <Button
              variant="secondary"
              leftIcon={<Plus size={16} />}
              onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}
            >
              New swap
            </Button>
          }
        />
        <SwapTokenCallout order={viewed} />
        <OrderDetail order={viewed} standalone />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <FindSwapBox />
      <Button
        leftIcon={<Plus size={16} />}
        onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}
        className="self-start"
      >
        Start a new swap
      </Button>
    </div>
  );
});

export default FindSwapPage;
